import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  FolderTree,
  ShoppingCart,
  BarChart3,
  Warehouse,
  TrendingUp,
  LogOut,
  Menu,
  X,
  Settings,
  QrCode,
  Image as ImageIcon,
  Users,
  History,
  KeyRound
} from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useState } from 'react';
import { cn } from '../lib/utils';
import NotificationBell from './NotificationBell';

const navItems = [
  { href: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  { href: '/admin/produtos', icon: Package, label: 'Produtos' },
  { href: '/admin/categorias', icon: FolderTree, label: 'Categorias' },
  { href: '/admin/pedidos', icon: ShoppingCart, label: 'Pedidos' },
  { href: '/admin/estoque', icon: Warehouse, label: 'Estoque' },
  { href: '/admin/compras', icon: TrendingUp, label: 'Compras' },
  { href: '/admin/financeiro', icon: BarChart3, label: 'Financeiro' },
  { href: '/admin/qr-pix', icon: QrCode, label: 'QR Code PIX' },
  { href: '/admin/banner', icon: ImageIcon, label: 'Página Inicial' },
  { href: '/admin/usuarios', icon: Users, label: 'Usuários' },
  { href: '/admin/logs', icon: History, label: 'Logs de Ações' },
  { href: '/admin/alterar-senha', icon: KeyRound, label: 'Alterar Senha' },
  { href: '/admin/configuracoes', icon: Settings, label: 'Configurações' },
];

const AdminLayout = ({ children, title }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-slate-900 text-white z-50 flex items-center justify-between px-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(true)}
          className="text-white hover:bg-slate-800"
        >
          <Menu className="h-6 w-6" />
        </Button>
        <h1 className="font-heading font-bold">KITS TOP Admin</h1>
        <NotificationBell variant="dark" />
      </header>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 h-full w-64 bg-slate-900 text-white z-50 transform transition-transform duration-300 lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gradient-hero flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-sm">KITS TOP</h1>
              <p className="text-xs text-slate-400">Painel Admin</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-slate-400 hover:text-white hover:bg-slate-800"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <nav className="p-2 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "admin-nav-item",
                  isActive && "active"
                )}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
              <span className="font-bold text-sm">{user?.name?.charAt(0)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={handleLogout}
            data-testid="admin-logout"
          >
            <LogOut className="w-5 h-5 mr-3" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        {/* Desktop top bar with notifications */}
        <div className="hidden lg:flex sticky top-0 z-30 h-14 items-center justify-end gap-2 px-6 bg-white border-b border-slate-200">
          <NotificationBell variant="light" />
        </div>
        <div className="p-4 lg:p-8">
          {title && (
            <h1 className="font-heading text-2xl font-bold text-foreground mb-6" data-testid="admin-page-title">
              {title}
            </h1>
          )}
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
